import React, { useEffect, useRef, useState, useCallback } from 'react'
import UnsavedChangesGuard from 'shared/components/UnsavedChangesGuard'
import { Button, Label, Message, Form } from 'semantic-ui-react'
import type { DropdownItemProps } from 'semantic-ui-react'
import type { FeedbackWidgetPreviewState } from './container'

import OptionsDropdown from 'components/OptionsDropdown'
import useModal from 'hooks/useModal'
import WidgetPreview from './WidgetPreview'
import CSSCustomStyle from 'components/CSSCustomStyle'
import { Loader } from 'shared/components/Loader'
import TestimonialWidgetFilter, { AnswerFilter } from 'organisms/TestimonialWidgetFilter'
import ProFeatureLabel from 'components/ProFeatureLabel'
import SaveAndPublishButton from './SaveAndPublishButton'
import WidgetForm from 'components/WidgetForm'
import WidgetPresetSelector from 'components/WidgetPresetSelector'
import ScoreWidgetFilter from 'organisms/ScoreWidgetFilter'
import { AllowPermission } from 'shared/components/AllowPermission'
import constants from 'config/constants'
import useAxios from 'hooks/useAxios'
import Title from 'shared/components/Title'
import ErrorText from 'shared/components/forms/ErrorText'
import { validateRequiredText } from 'libs/form-validation'

type FeedbackWidgetSettingsProps = {
  widgetApiResponse?: WidgetApiResponse
  setWidgetState: Function
  loading: boolean
  widgetState: WidgetSettingsState
  onSavingFeedbackWidgetSettings: () => void
  isSaveFeedbackWidgetSettingsLoading: boolean
  onRenamingFeedbackWidget: () => Promise<any>
  surveyOptions?: DropdownItemProps[]
  isRenameFeedbackWidgetLoading: boolean
  feedbackWidgetPreviewState: FeedbackWidgetPreviewState
  isWidgetCustomCSSPermissionEnabled: boolean
}

export type WidgetApiResponse = {
  id: string
  name: string
  type: WidgetType
  answer_filter: AnswerFilter
  font_style: string
  styles: object
  custom_css: string
  settings: object
}

export type WidgetType = 'masonry' | 'score' | 'carousel' | 'grid' | 'popup_box'

type WidgetLabelMap = {
  [key in WidgetType]?: string
}
export const widgetLabelMap: WidgetLabelMap = {
  masonry: 'Masonry',
  score: 'Score',
  carousel: 'Carousel',
  grid: 'Grid',
  popup_box: 'Popup box',
}

export default function FeedbackWidgetSettings({
  widgetId,
  widgetApiResponse,
  widgetState,
  setWidgetState,
  loading,
  onSavingFeedbackWidgetSettings,
  isSaveFeedbackWidgetSettingsLoading,
  onRenamingFeedbackWidget,
  isRenameFeedbackWidgetLoading,
  surveyOptions,
  feedbackWidgetPreviewState,
  isWidgetCustomCSSPermissionEnabled,
}: FeedbackWidgetSettingsProps) {
  const { open: openInstallationModal, Modal: InstallationModal } = useModal()

  const [hasWidgetFormUnsavedChange, setHasWidgetFormUnsavedChange] = useState(false)

  const { data: gridPresets } = useAxios({
    url: constants.widgetPresets.gridPresetsUrl,
  })
  const { data: carouselPresets } = useAxios({
    url: constants.widgetPresets.carouselPresetsUrl,
  })
  const { data: masonryPresets } = useAxios({
    url: constants.widgetPresets.masonryPresetsUrl,
  })
  const { data: popupBoxPresets } = useAxios({
    url: constants.widgetPresets.popupBoxPresetsUrl,
  })
  const { data: scorePresets } = useAxios({
    url: constants.widgetPresets.scorePresetsUrl,
  })

  useEffect(() => {
    // Prevent triggering onChange on default widget settings state
    // because it will then be triggered again after the API is loaded.
    if (widgetState && widgetState !== DEFAULT_WIDGET_SETTINGS_STATE) {
      setWidgetState?.(widgetState)
    }
  }, [setWidgetState, widgetState])

  useEffect(() => {
    if (widgetApiResponse) {
      setWidgetState(transformApiResponseToState(widgetApiResponse))
    }
  }, [widgetApiResponse, setWidgetState])

  function handleWidgetNameChange(name) {
    setWidgetState((prevData) => ({
      ...prevData,
      name: name,
    }))
  }

  const handleWidgetFormChange = useCallback(
    function handleWidgetFormChange({ styles, settings, fontStyle }) {
      setWidgetState((prevData) => ({
        ...prevData,
        styles,
      }))
      setWidgetState((prevData) => ({
        ...prevData,
        settings,
      }))
      setWidgetState((prevData) => ({
        ...prevData,
        fontStyle,
      }))
    },
    [setWidgetState]
  )

  const handleCustomCSSChange = useCallback(
    function handleCustomCSSChange(customCSS) {
      setWidgetState((prevData) => ({
        ...prevData,
        customCSS,
      }))
    },
    [setWidgetState]
  )

  if (loading) {
    return <Loader withContainer />
  }

  const disallowSavingCustomCSS =
    !isWidgetCustomCSSPermissionEnabled && Boolean(widgetState?.customCSS)

  return (
    <React.Fragment>
      <Title title={widgetState?.name} />
      <UnsavedChangesGuard
        when={hasWidgetFormUnsavedChange}
        message="You have unsaved changes. Would you like to proceed?"
      />
      <div className="items-baseline sm:flex">
        <h2 className="inline-block max-w-full overflow-hidden overflow-ellipsis whitespace-nowrap">
          {widgetState?.name}
        </h2>
        <Label basic className="!ml-4 !mr-auto">
          {widgetLabelMap[widgetState?.type as string]}
        </Label>
        <div className="flex flex-shrink-0 ml-4">
          <Button className="!block !mr-0 !mt-4" onClick={openInstallationModal}>
            Get installation code
          </Button>
          <AllowPermission permission={constants.permissions.manageFeedbackWidgets}>
            <SaveAndPublishButton
              className="!mx-0 !ml-4 !mr-0 !mt-4"
              onSavingFeedbackWidgetSettings={onSavingFeedbackWidgetSettings}
              disallowSavingCustomCSS={disallowSavingCustomCSS}
              isSaveFeedbackWidgetSettingsLoading={isSaveFeedbackWidgetSettingsLoading}
              setHasWidgetFormUnsavedChange={setHasWidgetFormUnsavedChange}
            />
            <WidgetOptions
              className="!ml-4 !mt-4"
              onRenamingFeedbackWidget={onRenamingFeedbackWidget}
              isRenameFeedbackWidgetLoading={isRenameFeedbackWidgetLoading}
              name={widgetState?.name}
              onWidgetNameChange={handleWidgetNameChange}
            />
          </AllowPermission>
        </div>
      </div>
      <a href="https://help.simplesat.io/en/articles/843342" target="_blank" rel="noopener">
        <i className="icon far fa-book" />
        Learn more about customizing widgets
      </a>
      <hr />

      <WidgetForm
        styles={widgetState?.styles}
        settings={widgetState?.settings}
        fontStyle={widgetState?.fontStyle}
        widgetType={widgetState?.type}
        onChange={handleWidgetFormChange}
        setHasWidgetFormUnsavedChange={setHasWidgetFormUnsavedChange}
        surveyOptions={surveyOptions}
        additionalAccordions={[
          {
            title: {
              children: (
                <span>
                  Custom CSS{' '}
                  <ProFeatureLabel
                    title="Custom CSS"
                    content="Custom CSS gives you full control over your feedback widget's styling and design."
                    learnMoreLink="https://help.simplesat.io/en/articles/843342-publishing-customer-feedback-and-ratings#style-a-widget-with-custom-css"
                    shouldShowBillingPageLink={isWidgetCustomCSSPermissionEnabled === false}
                  />
                </span>
              ),
            },
            content: {
              children: (
                <Form>
                  <CSSCustomStyle
                    customCSS={widgetState?.customCSS}
                    setCustomCSS={handleCustomCSSChange}
                  />
                </Form>
              ),
            },
          },
          {
            title: {
              children: 'Feedback filtering',
            },
            content: {
              children:
                widgetState?.type === 'score' ? (
                  <ScoreWidgetFilter
                    widgetSettingsState={widgetState}
                    setWidgetState={setWidgetState}
                  />
                ) : (
                  <TestimonialWidgetFilter
                    widgetSettingsState={widgetState}
                    setWidgetState={setWidgetState}
                    surveyOptions={surveyOptions}
                  />
                ),
            },
          },
        ]}
      />

      <WidgetPresetSelector
        className="mt-5"
        type={widgetState?.type}
        onSelect={({ styles, settings, font_style }) => {
          const newSettings =
            widgetState?.type === 'score'
              ? {
                  ...settings,
                  //preset doesn't apply (0-source-00-survey) value for score widget
                  '0-source-00-survey': widgetState?.settings['0-source-00-survey'],
                }
              : {
                  ...settings,
                  //preset doesn't apply (0-global-05-max-feedback) value for all widget without score
                  '0-global-05-max-feedback': widgetState?.settings['0-global-05-max-feedback'],
                }
          handleWidgetFormChange({
            styles,
            settings: newSettings,
            fontStyle: font_style,
          })
        }}
        gridPresets={gridPresets}
        carouselPresets={carouselPresets}
        masonryPresets={masonryPresets}
        popupBoxPresets={popupBoxPresets}
        scorePresets={scorePresets}
      />
      <hr />
      <WidgetPreview feedbackWidgetPreviewState={feedbackWidgetPreviewState} />
      <InstallationModal size="small">
        <InstallationModal.Header>Install your feedback widget</InstallationModal.Header>
        <InstallationModal.Content>
          <a
            href="https://help.simplesat.io/en/articles/843342-publishing-customer-feedback-and-ratings#install-a-widget-on-your-website"
            target="_blank"
            rel="noopener"
          >
            <i className="icon far fa-book" />
            How to add feedback widgets to your website
          </a>
          <h3>Step 1 - Copy the embed code</h3>
          <WidgetCode widgetId={widgetId} />
          <hr />
          <h3>Step 2 - Add the code to your website</h3>
          <p>Paste the code directly on your website where you'd like widget to be displayed.</p>
          <p>
            You might need your website developer to help out with this. If you have any questions,
            feel free to reach out to the Simplesat team for help.
          </p>
          <Message content="You only need to add this once. After it's installed, any changes you make will automatically appear on your website. 😌" />
        </InstallationModal.Content>
      </InstallationModal>
    </React.Fragment>
  )
}

export type WidgetSettingsState = {
  name: string
  fontStyle: string
  customCSS: string
  styles: object
  settings: object
  answerFilter?: AnswerFilter
  type: string
}

const DEFAULT_WIDGET_SETTINGS_STATE: WidgetSettingsState = {
  name: '',
  fontStyle: '',
  customCSS: '',
  styles: {},
  settings: {},
  answerFilter: undefined,
}

function WidgetOptions({
  className,
  onRenamingFeedbackWidget,
  isRenameFeedbackWidgetLoading,
  name,
  onWidgetNameChange,
}) {
  const widgetRenamingModal = useModal()
  const widgetDeletionModal = useModal()
  const [nameError, setNameError] = useState('')

  function handleCloseRenameModal() {
    setNameError('')
    widgetRenamingModal.close()
  }

  function handleSubmitRename() {
    const error = validateRequiredText('Widget name', name)
    if (error) {
      setNameError(error)
      return
    }
    onRenamingFeedbackWidget().then(handleCloseRenameModal)
  }

  const widgetOptions = [
    {
      icon: 'far fa-edit',
      key: 'rename',
      text: 'Rename',
      onClick: () => {
        setNameError('')
        widgetRenamingModal.open()
      },
    },
    // TODO: Add function to delete widget in widget settings page
    // {
    //   className: '!text-red',
    //   icon: 'trash alternate',
    //   key: 'delete',
    //   text: 'Delete widget',
    //   onClick: widgetDeletionModal.open,
    // },
  ]

  const Modal = widgetRenamingModal.Modal

  return (
    <React.Fragment>
      <OptionsDropdown className={className} options={widgetOptions} />
      <widgetRenamingModal.Modal size="tiny">
        <Modal.Header>Rename widget</Modal.Header>

        <Modal.Content>
          <Form id="widget-rename-form" autoComplete="off" noValidate onSubmit={handleSubmitRename}>
            <Form.Field error={Boolean(nameError)}>
              <label htmlFor="widget-rename-name-input">Widget name</label>
              <div className="ui input">
                <input
                  id="widget-rename-name-input"
                  type="text"
                  autoComplete="off"
                  value={name}
                  onChange={(e) => {
                    onWidgetNameChange(e.target.value)
                    if (nameError) {
                      setNameError('')
                    }
                  }}
                />
              </div>
              {nameError && <ErrorText>{nameError}</ErrorText>}
            </Form.Field>
          </Form>
        </Modal.Content>

        <Modal.Actions>
          <Button onClick={handleCloseRenameModal}>Cancel</Button>
          <Button
            primary
            type="submit"
            form="widget-rename-form"
            disabled={isRenameFeedbackWidgetLoading}
            loading={isRenameFeedbackWidgetLoading}
          >
            Save
          </Button>
        </Modal.Actions>
      </widgetRenamingModal.Modal>

      <widgetDeletionModal.Modal size="tiny">
        <Modal.Header>Delete widget</Modal.Header>

        <Modal.Content>
          You’re going to delete this widget. All settings of this widget will be deleted and the
          widgets you’ve already installed on your website will be disappear.
        </Modal.Content>

        <Modal.Actions>
          <Button onClick={widgetDeletionModal.close}>Cancel</Button>
          <Button negative>Delete widget</Button>
        </Modal.Actions>
      </widgetDeletionModal.Modal>
    </React.Fragment>
  )
}

function transformApiResponseToState(widgetApiResponse: WidgetApiResponse): WidgetSettingsState {
  return {
    name: widgetApiResponse?.name,
    settings: widgetApiResponse?.settings,
    styles: widgetApiResponse?.styles,
    fontStyle: widgetApiResponse?.font_style,
    customCSS: widgetApiResponse?.custom_css,
    answerFilter: widgetApiResponse?.answer_filter,
    type: widgetApiResponse?.type,
  }
}

function WidgetCode({ widgetId }) {
  const widgetCode = `<a href="https://simplesat.io" data-widget-id="${widgetId}"></a>
<script id='simplesat-widget-script' src="${process.env.REACT_APP_WIDGET_SCRIPT_URL}"></script>`
  const embedCodeRef = useRef<HTMLTextAreaElement>(null)
  function selectCode(e) {
    e.target.select()
  }
  return (
    <Form>
      <Form.Field>
        <textarea
          ref={embedCodeRef}
          readOnly
          rows={3}
          className="w-full"
          value={widgetCode}
          onClick={selectCode}
        />
      </Form.Field>
      <CopyWidgetCodeButton embedCodeRef={embedCodeRef} />
    </Form>
  )
}

type CopyWidgetCodeButtonProps = {
  embedCodeRef: React.RefObject<HTMLTextAreaElement>
}
function CopyWidgetCodeButton({ embedCodeRef }: CopyWidgetCodeButtonProps) {
  const TIMEOUT_MS = 2000
  const [overriddenButtonLabel, setOverriddenButtonLabel] = useDisappearingText('', TIMEOUT_MS)

  function handleClick() {
    copyWidgetCode()
    setOverriddenButtonLabel('Copied!')
  }

  function copyWidgetCode() {
    // Newer standard to replace `execCommand`
    if (navigator.clipboard && embedCodeRef.current) {
      navigator.clipboard.writeText(embedCodeRef.current.value)
    } else {
      embedCodeRef.current?.select()
      document.execCommand('copy')
    }
  }

  return (
    <Button size="large" fluid primary onClick={handleClick}>
      {overriddenButtonLabel || 'Copy widget code'}
    </Button>
  )
}

function useDisappearingText(initialText, timeout) {
  const [text, setText] = useState(initialText)
  const initialTimerId = 0
  const timerRef = useRef(initialTimerId)

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current)
    }
  }, [])

  function setTextWithTimeout(text) {
    setText(text)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      setText('')
    }, timeout)
  }

  return [text, setTextWithTimeout]
}



// WEBPACK FOOTER //
// ./src/components/Admin/FeedbackWidgetSettings/index.tsx